import { createFileRoute, Outlet, redirect, useParams } from '@tanstack/react-router'
import { useState } from 'react'
import { apiClient } from '@/lib/api/client'
import { queryKeys } from '@/lib/api/queries'
import { SupportChatDrawer, SupportChatTrigger } from '@/components/support-chat'

// Simple in-memory cache to prevent repeated verification during navigation
// Key: `${token_prefix}:${eventId}`, Value: { event, hasAccess, timestamp }
const verificationCache = new Map<string, { event: unknown; hasAccess: boolean; timestamp: number }>()
const CACHE_TTL = 30 * 1000 // 30 seconds - short enough to respect permission changes

async function verifyEventAccess(token: string, eventId: number): Promise<{ event: unknown; hasAccess: boolean; error?: string }> {
  // Check cache first (use first 20 chars of token as key prefix for privacy)
  const cacheKey = `${token.slice(0, 20)}:${eventId}`
  const cached = verificationCache.get(cacheKey)
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    console.log(`✅ Using cached verification for event ${eventId}`)
    return { event: cached.event, hasAccess: cached.hasAccess }
  }
  try {
    // Fetch event first
    const event = await apiClient.getEvent(token, eventId)
    
    // IMPORTANT: The event endpoint may return data even for unauthorized users
    // Some APIs return empty data instead of 403, so we use transaction count
    // as a reliable permission check (it returns 403 for unauthorized users)
    try {
      await apiClient.getTransactionCount(token, eventId)
      console.log(`✅ User has verified permissions for event ${eventId}`)
      // Cache successful verification
      verificationCache.set(cacheKey, { event, hasAccess: true, timestamp: Date.now() })
      return { event, hasAccess: true }
    } catch (permError) {
      const permErrorMessage = permError instanceof Error ? permError.message : String(permError)
      if (permErrorMessage.includes('403')) {
        console.warn(`🚫 User lacks permissions for event ${eventId} (transaction count returned 403)`)
        // Cache permission denial (shorter TTL)
        verificationCache.set(cacheKey, { event: null, hasAccess: false, timestamp: Date.now() })
        return { event: null, hasAccess: false, error: '403' }
      }
      // Non-403 errors from transaction count are not permission issues, continue
      console.warn(`⚠️ Transaction count check failed with non-403 error: ${permErrorMessage}`)
      // Cache as successful since it's not a permission error
      verificationCache.set(cacheKey, { event, hasAccess: true, timestamp: Date.now() })
      return { event, hasAccess: true }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)
    console.error(`Event access check failed for event ${eventId}:`, errorMessage)
    
    if (errorMessage.includes('403')) {
      return { event: null, hasAccess: false, error: '403' }
    }
    if (errorMessage.includes('404')) {
      return { event: null, hasAccess: false, error: '404' }
    }
    
    return { event: null, hasAccess: false, error: errorMessage }
  }
}

export const Route = createFileRoute('/_authenticated/events/$eventId')({
  beforeLoad: async ({ params, context }) => {
    const eventId = Number(params.eventId)
    
    // Skip if no token (shouldn't happen since we're under _authenticated)
    if (!context.auth.token) {
      throw redirect({ to: '/auth/login', search: { redirect: `/events/${eventId}/dashboard`, activated: undefined } })
    }
    
    const { event, hasAccess, error } = await verifyEventAccess(context.auth.token, eventId)
    
    if (!hasAccess) {
      console.warn(`🚫 Access denied to event ${eventId} (${error}) - redirecting to events list`)
      throw redirect({ to: '/events' })
    }
    
    // Prime the React Query cache so child routes don't re-fetch
    context.queryClient.setQueryData(queryKeys.event(eventId), event)
    
    // Return event for use in loader data
    return { event }
  },
  component: EventLayout,
})

function EventLayout() {
  // User has been verified to have access in beforeLoad
  // Child routes can access the event via useRouteContext or useEvent (cached)
  const { eventId } = useParams({ from: '/_authenticated/events/$eventId' })
  const eventIdNum = Number(eventId)
  const [chatOpen, setChatOpen] = useState(false)
  // Drawer stays mounted once first opened so minimize preserves the
  // session, messages, and any in-flight stream. Unmounts naturally when
  // the operator navigates to a different event (new EventLayout instance).
  const [chatMounted, setChatMounted] = useState(false)

  const openChat = () => {
    setChatMounted(true)
    setChatOpen(true)
  }

  return (
    <>
      <Outlet />
      {!chatOpen && <SupportChatTrigger onClick={openChat} />}
      {chatMounted && (
        <SupportChatDrawer
          eventId={eventIdNum}
          isOpen={chatOpen}
          onClose={() => setChatOpen(false)}
        />
      )}
    </>
  )
}
